import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowRight, Menu, X } from "lucide-react";
import { toast } from "sonner";
import OptimizedImage from "@/components/OptimizedImage";
import { useSignIn } from "@/hooks/useSignIn";

interface HeaderProps {
  onSignIn: () => void;
}

const Header = ({ onSignIn }: HeaderProps) => {
  const [isHovered, setIsHovered] = React.useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { signingIn } = useSignIn();
  
  // Wrap onSignIn to add loading state handling
  const handleSignInClick = async () => {
    console.log('Header handleSignInClick called, signingIn:', signingIn);
    if (signingIn) {
      console.log('Already signing in, returning early');
      return;
    }
    console.log('Calling onSignIn prop...');
    try {
      await onSignIn();
      console.log('onSignIn completed');
    } catch (error) {
      console.error('Error in handleSignInClick:', error);
    }
  };

  // Track scroll position for header styling
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const scrollToSection = (sectionId: string) => {
    const isMobile = window.innerWidth < 768;
    const wasMenuOpen = isMobileMenuOpen;
    
    // Close mobile menu first if open, so layout updates before scroll calculation
    if (wasMenuOpen) {
      setIsMobileMenuOpen(false);
    }

    // Calculate header height dynamically
    const getHeaderHeight = () => {
      const header = document.querySelector('header');
      if (!header) return 100;
      
      const isMobile = window.innerWidth < 768;
      const headerHeight = header.offsetHeight;
      
      // Mobile: use a fixed smaller offset since scroll-mt-24 (96px) on pricing section
      // already accounts for spacing. A smaller offset prevents scrolling too high.
      // Desktop: standard offset with padding
      return isMobile ? 70 : headerHeight + 20;
    };

    // Calculate mobile offset accounting for header and banner
    const getMobileOffset = () => {
      const header = document.querySelector('header');
      // Find the special offer banner by looking for the element with bg-gradient-to-r text-white
      const banner = Array.from(document.querySelectorAll('div')).find(
        (el) => el.classList.contains('bg-gradient-to-r') && 
                el.classList.contains('text-white') &&
                el.classList.contains('static')
      );
      
      let offset = 0;
      
      // Add header height
      if (header) {
        offset += header.offsetHeight;
      }
      
      // Add banner height on mobile (banner is static on mobile)
      if (banner && isMobile) {
        offset += banner.offsetHeight;
      }
      
      // Add small padding for better visual spacing
      offset += 10;
      
      return offset;
    };

    // Scroll function that will be called after menu closes (if needed)
    const performScroll = () => {
      // If not on home page, navigate there first
      if (location.pathname !== '/') {
        navigate('/');
        // Wait for navigation then scroll
        setTimeout(() => {
          const element = document.getElementById(sectionId);
          if (element) {
            if (isMobile) {
              // On mobile, use precise calculation to ensure exact positioning
              const mobileOffset = getMobileOffset();
              const elementPosition = element.getBoundingClientRect().top;
              const offsetPosition = elementPosition + window.pageYOffset - mobileOffset;
              
              // Use requestAnimationFrame for smoother scroll on mobile
              requestAnimationFrame(() => {
                window.scrollTo({
                  top: Math.max(0, offsetPosition),
                  behavior: 'smooth'
                });
              });
            } else {
              const headerOffset = getHeaderHeight();
              const elementPosition = element.getBoundingClientRect().top;
              const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
              window.scrollTo({
                top: offsetPosition,
                behavior: 'smooth'
              });
            }
          }
        }, 100);
      } else {
        // Already on home page, just scroll
        // If menu was open, wait a bit for layout to settle
        const delay = wasMenuOpen ? 150 : 0;
        setTimeout(() => {
          const element = document.getElementById(sectionId);
          if (element) {
            if (isMobile) {
              // On mobile, use precise calculation to ensure exact positioning
              const mobileOffset = getMobileOffset();
              const elementPosition = element.getBoundingClientRect().top;
              const offsetPosition = elementPosition + window.pageYOffset - mobileOffset;
              
              // Use requestAnimationFrame for smoother scroll on mobile
              requestAnimationFrame(() => {
                window.scrollTo({
                  top: Math.max(0, offsetPosition),
                  behavior: 'smooth'
                });
              });
            } else {
              const headerOffset = getHeaderHeight();
              const elementPosition = element.getBoundingClientRect().top;
              const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
              window.scrollTo({
                top: offsetPosition,
                behavior: 'smooth'
              });
            }
          }
        }, delay);
      }
    };

    // If menu was open, wait for it to close before scrolling
    if (wasMenuOpen) {
      setTimeout(performScroll, 150);
    } else {
      performScroll();
    }
  };

  const handleBlogClick = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsMobileMenuOpen(false);
    navigate('/blog');
  };

  const handleLogoClick = () => {
    navigate('/');
    window.scrollTo(0, 0);
  };

  // Check if we're on a blog post page
  const isBlogPost = location.pathname.startsWith('/blog/') && location.pathname !== '/blog';
  
  return (
    <header className={`${isBlogPost ? 'static' : 'sticky top-0 sm:top-[48px]'} left-0 right-0 w-full flex justify-center z-50 px-4 sm:px-6 lg:px-8 py-4 transition-all duration-300`}>
      {/* Desktop Header */}
      <div className={`hidden md:grid md:grid-cols-[auto_1fr_auto] items-center rounded-full border border-border/50 px-8 py-3 w-full max-w-[80rem] transition-all duration-300 ${
        isScrolled 
          ? 'bg-background/90 backdrop-blur-sm shadow-lg' 
          : 'bg-background'
      }`}>
        <div className="flex items-center">
          <button
            onClick={handleLogoClick}
            className="relative group"
          >
            <div
              className="h-16 w-16"
              style={{
                transform: isHovered
                  ? 'translateZ(0) scale(1.05) rotate(2deg)'
                  : 'translateZ(0) scale(1) rotate(0deg)',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                cursor: 'pointer'
              }}
              onMouseEnter={() => setIsHovered(true)}
              onMouseLeave={() => setIsHovered(false)}
            >
              <OptimizedImage
                src="https://ancujowsxivyfnxhnsmr.supabase.co/storage/v1/object/public/images/Untitled-222.png"
                alt="Seoro Logo"
                width={64}
                height={64}
                priority={true}
                className="h-full w-full object-contain"
              />
            </div>
            <div
              className="absolute inset-0 bg-gradient-to-br from-primary/10 to-primary/3 rounded-lg -z-10"
              style={{
                opacity: isHovered ? 0.6 : 0,
                transition: 'opacity 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                filter: 'blur(15px)',
                transform: 'translateZ(0)'
              }}
            />
          </button>
        </div>

        <div className="flex items-center justify-center space-x-6">
          <Button
            variant="ghost"
            onClick={() => scrollToSection('how-it-works')}
            className="text-sm font-semibold text-foreground/80 hover:bg-gray-50 hover:shadow-sm transition-all duration-300 px-4 py-2.5 rounded-xl backdrop-blur-sm border border-transparent hover:border-gray-300/50 tracking-wide"
          >
            How it works
          </Button>
          <Button
            variant="ghost"
            onClick={() => scrollToSection('writing-examples')}
            className="text-sm font-semibold text-foreground/80 hover:bg-gray-50 hover:shadow-sm transition-all duration-300 px-4 py-2.5 rounded-xl backdrop-blur-sm border border-transparent hover:border-gray-300/50 tracking-wide"
          >
            Writing Examples
          </Button>
          <Button
            variant="ghost"
            onClick={() => scrollToSection('pricing')}
            className="text-sm font-semibold text-foreground/80 hover:bg-gray-50 hover:shadow-sm transition-all duration-300 px-4 py-2.5 rounded-xl backdrop-blur-sm border border-transparent hover:border-gray-300/50 tracking-wide"
          >
            Pricing
          </Button>
          <Button
            variant="ghost"
            onClick={handleBlogClick}
            className="text-sm font-semibold text-foreground/80 hover:bg-gray-50 hover:shadow-sm transition-all duration-300 px-4 py-2.5 rounded-xl backdrop-blur-sm border border-transparent hover:border-gray-300/50 tracking-wide"
          >
            Blog
          </Button>
        </div>
        <div className="flex items-center space-x-4">
          <Button
            onClick={handleSignInClick}
            disabled={signingIn}
            variant="outline"
            className="rounded-full px-8 py-4 text-sm font-semibold border-2 border-gray-300 text-gray-700 hover:bg-gray-50 hover:border-gray-400 transition-all duration-300 flex items-center justify-center gap-3 min-h-[48px] backdrop-blur-sm bg-background/80 disabled:opacity-50 disabled:cursor-not-allowed"
          >
              <OptimizedImage
                src="https://ancujowsxivyfnxhnsmr.supabase.co/storage/v1/object/public/images/google.82c899b0.svg"
                alt="Google"
                width={20}
                height={20}
                priority={true}
                className="h-5 w-5"
              />
            Join with Google
          </Button>
          <Button
            onClick={handleSignInClick}
            disabled={signingIn}
            className="rounded-full px-8 py-4 text-sm font-semibold bg-gradient-to-r from-primary via-primary to-primary/90 text-primary-foreground hover:from-primary/95 hover:via-primary/90 hover:to-primary/85 transition-all duration-300 flex items-center justify-center gap-3 min-h-[48px] relative overflow-hidden group shadow-sm hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:shadow-sm"
          >
                            <span className="relative z-10 font-semibold tracking-wide">Get Started Now</span>
            <ArrowRight className="h-5 w-5 group-hover:translate-x-1 transition-transform duration-200 relative z-10" />
            <div className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/10 to-white/0 transform translate-x-[-100%] group-hover:translate-x-0 transition-transform duration-500"></div>
          </Button>
        </div>
      </div>

      {/* Mobile Header */}
      <div className="md:hidden w-full max-w-6xl">
        {/* Mobile Header Bar */}
        <div className={`flex items-center justify-between rounded-full border border-border/50 px-4 py-3 transition-all duration-300 ${
          isScrolled 
            ? 'bg-background/90 backdrop-blur-sm shadow-lg' 
            : 'bg-background'
        }`}>
          {/* Logo */}
          <div className="flex items-center">
            <button
              onClick={handleLogoClick}
              className="relative group"
            >
              <div
                className="h-12 w-12 sm:h-14 sm:w-14"
                style={{
                  transform: isHovered
                    ? 'translateZ(0) scale(1.05) rotate(2deg)'
                    : 'translateZ(0) scale(1) rotate(0deg)',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  cursor: 'pointer'
                }}
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
              >
                <img
                  src="https://ancujowsxivyfnxhnsmr.supabase.co/storage/v1/object/public/images/Untitled-222.png"
                  alt="Seoro Logo"
                  className="h-full w-full object-contain"
                />
              </div>
              <div
                className="absolute inset-0 bg-gradient-to-br from-primary/10 to-primary/3 rounded-lg -z-10"
                style={{
                  opacity: isHovered ? 0.6 : 0,
                  transition: 'opacity 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  filter: 'blur(15px)',
                  transform: 'translateZ(0)'
                }}
              />
            </button>
          </div>

          {/* Mobile CTA Button */}
          <div className="flex items-center gap-3">
            <Button
              onClick={handleSignInClick}
              size="sm"
              className="rounded-full px-5 py-2.5 text-sm font-semibold bg-gradient-to-r from-primary via-primary to-primary/90 text-primary-foreground hover:from-primary/95 hover:via-primary/90 hover:to-primary/85 transition-all duration-300 flex items-center justify-center gap-2 relative overflow-hidden group hover:scale-105 backdrop-blur-sm"
            >
              <span className="relative z-10 font-semibold tracking-wide">Get Started Now</span>
              <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform duration-200 relative z-10" />
              <div className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/15 to-white/0 transform translate-x-[-100%] group-hover:translate-x-0 transition-transform duration-500"></div>
            </Button>

            {/* Mobile Menu Button */}
            <Button
              variant="ghost"
              size="sm"
              onClick={toggleMobileMenu}
              className="p-2.5 rounded-full hover:bg-gray-100/80 transition-all duration-200 border border-gray-200/50 backdrop-blur-sm hover:shadow-md hover:scale-105"
            >
              {isMobileMenuOpen ? (
                <X className="h-5 w-5 text-gray-700" />
              ) : (
                <Menu className="h-5 w-5 text-gray-700" />
              )}
            </Button>
          </div>
        </div>

        {/* Mobile Menu Dropdown */}
        {isMobileMenuOpen && (
          <div className="mt-3 bg-background/95 backdrop-blur-xl rounded-3xl border border-border/40 shadow-2xl overflow-hidden animate-in slide-in-from-top-2 duration-300">
            {/* Navigation Links */}
            <div className="p-6 space-y-1">
              <Button
                variant="ghost"
                onClick={() => scrollToSection('how-it-works')}
                className="w-full justify-start text-base font-semibold text-foreground/90 hover:bg-primary/5 hover:text-primary transition-all duration-300 px-5 py-4 rounded-2xl group"
              >
                <span className="group-hover:translate-x-1 transition-transform duration-200">How it works</span>
              </Button>
              <Button
                variant="ghost"
                onClick={() => scrollToSection('writing-examples')}
                className="w-full justify-start text-base font-semibold text-foreground/90 hover:bg-primary/5 hover:text-primary transition-all duration-300 px-5 py-4 rounded-2xl group"
              >
                <span className="group-hover:translate-x-1 transition-transform duration-200">Writing Examples</span>
              </Button>
              <Button
                variant="ghost"
                onClick={() => scrollToSection('pricing')}
                className="w-full justify-start text-base font-semibold text-foreground/90 hover:bg-primary/5 hover:text-primary transition-all duration-300 px-5 py-4 rounded-2xl group"
              >
                <span className="group-hover:translate-x-1 transition-transform duration-200">Pricing</span>
              </Button>
              <Button
                variant="ghost"
                onClick={handleBlogClick}
                className="w-full justify-start text-base font-semibold text-foreground/90 hover:bg-primary/5 hover:text-primary transition-all duration-300 px-5 py-4 rounded-2xl group"
              >
                <span className="group-hover:translate-x-1 transition-transform duration-200">Blog</span>
              </Button>
            </div>

            {/* Mobile CTA Buttons */}
            <div className="p-6 space-y-4 border-t border-border/20 bg-gradient-to-b from-transparent to-muted/20">
              <Button
                onClick={handleSignInClick}
                variant="outline"
                className="w-full rounded-2xl px-6 py-4 text-base font-semibold border-2 border-gray-300 text-gray-700 hover:bg-gray-50 hover:border-gray-400 transition-all duration-300 flex items-center justify-center gap-3 backdrop-blur-sm bg-background/90 hover:shadow-lg hover:scale-[1.02] group"
              >
                <img
                  src="https://ancujowsxivyfnxhnsmr.supabase.co/storage/v1/object/public/images/google.82c899b0.svg"
                  alt="Google"
                  className="h-5 w-5 group-hover:scale-110 transition-transform duration-200"
                />
                Join with Google
              </Button>
              <Button
                onClick={handleSignInClick}
                className="w-full rounded-2xl px-6 py-4 text-base font-semibold bg-gradient-to-r from-primary via-primary to-primary/90 text-primary-foreground hover:from-primary/95 hover:via-primary/90 hover:to-primary/85 transition-all duration-300 flex items-center justify-center gap-3 relative overflow-hidden group shadow-lg hover:shadow-xl hover:scale-[1.02]"
              >
                <span className="relative z-10 font-semibold tracking-wide">Get Started Now</span>
                <ArrowRight className="h-5 w-5 group-hover:translate-x-1 transition-transform duration-200 relative z-10" />
                <div className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/15 to-white/0 transform translate-x-[-100%] group-hover:translate-x-0 transition-transform duration-500"></div>
              </Button>
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
